import { useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Headset, ArrowSquareOut, MagnifyingGlass } from "@phosphor-icons/react";
import { BackupHealthCard } from "@/components/BackupHealthCard";

import SLAConfig from "@/pages/SLAConfig";
import Groups from "@/pages/Groups";
import CannedResponses from "@/pages/CannedResponses";
import EmailIngest from "@/pages/EmailIngest";
import AlertEvents from "@/pages/AlertEvents";
import ScimSettings from "@/pages/ScimSettings";
import TeamsIntegration from "@/pages/TeamsIntegration";
import Notifications from "@/pages/Notifications";
import BackupRestore from "@/pages/BackupRestore";
import HolidayCalendar from "@/pages/HolidayCalendar";

// Alphabetical by label.
const TABS = [
  { value: "alert-webhook", label: "Alert Webhook", roles: ["admin"], render: () => <AlertEvents /> },
  { value: "assignment-groups", label: "Assignment Groups", roles: ["admin", "manager"], render: () => <Groups /> },
  { value: "backup-restore", label: "Backup & Restore", roles: ["admin"], render: () => <BackupRestore /> },
  { value: "canned-responses", label: "Canned Responses", roles: ["admin", "manager", "agent"], render: () => <CannedResponses /> },
  { value: "email-ingest", label: "Email Ingest", roles: ["admin"], render: () => <EmailIngest /> },
  { value: "email-notifications", label: "Email Notifications", roles: ["admin"], render: () => <Notifications /> },
  { value: "end-user-portal", label: "End-User Portal", roles: ["admin", "manager", "agent"], render: () => <PortalLauncher /> },
  { value: "holiday-calendar", label: "Holiday Calendar", roles: ["admin", "timesheet_admin"], render: () => <HolidayCalendar /> },
  { value: "scim", label: "SCIM Provisioning", roles: ["admin"], render: () => <ScimSettings /> },
  { value: "sla", label: "SLA Policies", roles: ["admin"], render: () => <SLAConfig /> },
  { value: "teams", label: "Teams Integration", roles: ["admin"], render: () => <TeamsIntegration /> },
];

function PortalLauncher() {
  const nav = useNavigate();
  return (
    <div className="p-6" data-testid="admin-portal-launcher">
      <Card className="p-8 border-border rounded-sm max-w-xl">
        <div className="flex items-center gap-2 mb-2">
          <Headset size={18} className="text-primary" />
          <span className="text-[11px] font-mono uppercase tracking-widest text-primary">Self-Service</span>
        </div>
        <h2 className="font-display text-2xl font-black">End-User Portal</h2>
        <p className="text-sm text-muted-foreground mt-2">Open the self-service portal that end-users see to submit and track tickets and browse the knowledge base.</p>
        <Button data-testid="admin-open-portal-btn" onClick={() => nav("/portal")} className="mt-5 rounded-sm font-mono text-xs uppercase tracking-widest">
          <ArrowSquareOut size={15} className="mr-2" /> Open End-User Portal
        </Button>
      </Card>
    </div>
  );
}

export default function AdminHub() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState("");
  const tabs = useMemo(() => TABS.filter((t) => t.roles.includes(user?.role)), [user?.role]);

  const requested = params.get("tab");
  const active = tabs.some((t) => t.value === requested) ? requested : (tabs[0]?.value || "sla");
  const setActive = (v) => { const p = new URLSearchParams(params); p.set("tab", v); setParams(p, { replace: true }); };

  if (tabs.length === 0) return <div className="p-6 text-muted-foreground">No administration tools available for your role.</div>;

  const query = q.trim().toLowerCase();
  const filtered = query ? tabs.filter((t) => t.label.toLowerCase().includes(query)) : tabs;
  const jumpFirst = () => { if (filtered.length) { setActive(filtered[0].value); setQ(""); } };

  return (
    <div data-testid="admin-hub">
      <Tabs value={active} onValueChange={setActive} className="w-full">
        <div className="px-6 pt-6">
          {user?.role === "admin" && <div className="mb-4"><BackupHealthCard /></div>}
          <div className="flex items-center justify-between gap-4 mb-2">
            <div className="text-[11px] font-mono uppercase tracking-widest text-primary">Administration</div>
            <div className="relative w-64 max-w-[50vw]">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <Input
                data-testid="admin-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") jumpFirst(); }}
                placeholder="Search settings…"
                className="h-8 pl-8 rounded-sm text-sm"
              />
            </div>
          </div>
          <TabsList data-testid="admin-hub-tabs" className="flex-wrap h-auto">
            {filtered.length === 0 ? (
              <span className="px-3 py-1.5 text-xs text-muted-foreground" data-testid="admin-search-empty">No settings match "{q}"</span>
            ) : filtered.map((t) => (
              <TabsTrigger key={t.value} value={t.value} data-testid={`admin-hub-tab-${t.value}`}>{t.label}</TabsTrigger>
            ))}
          </TabsList>
        </div>
        {tabs.map((t) => (
          <TabsContent key={t.value} value={t.value} className="mt-0">
            {t.render()}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
